import { motion } from "framer-motion";
import {
  ShieldCheck,
  Cloud,
  Radar,
  KeyRound,
  Brain,
  ServerCog,
} from "lucide-react";

const CATEGORIES = [
  {
    code: "01",
    title: "Microsoft Security",
    icon: ShieldCheck,
    accent: "blue",
    skills: [
      "Defender XDR",
      "Sentinel",
      "Purview",
      "Security Copilot",
      "Exchange Online",
      "Microsoft 365 Security",
    ],
  },
  {
    code: "02",
    title: "Cloud Security",
    icon: Cloud,
    accent: "cyan",
    skills: [
      "Azure Security",
      "AWS Security",
      "Wiz CNAPP",
      "Cloud Security Architecture",
    ],
  },
  {
    code: "03",
    title: "SOC & Threat Operations",
    icon: Radar,
    accent: "indigo",
    skills: [
      "SIEM / SOAR",
      "Threat Hunting",
      "KQL",
      "Incident Response",
      "Splunk",
      "CrowdStrike NG-SIEM",
    ],
  },
  {
    code: "04",
    title: "Identity & Endpoint",
    icon: KeyRound,
    accent: "blue",
    skills: [
      "Entra ID",
      "Intune",
      "Active Directory",
      "SCCM",
      "Endpoint Security",
    ],
  },
  {
    code: "05",
    title: "AI Security",
    icon: Brain,
    accent: "cyan",
    skills: [
      "AI Governance",
      "GenAI Defense",
      "LLM Security",
      "AI Risk Management",
    ],
  },
  {
    code: "06",
    title: "Enterprise Infrastructure & ITSM",
    icon: ServerCog,
    accent: "indigo",
    skills: [
      "Windows Server",
      "PowerShell",
      "ServiceNow",
      "ITIL",
      "Networking",
    ],
  },
];

const accentMap = {
  blue: {
    dot: "#60A5FA",
    border: "rgba(96,165,250,0.30)",
    soft: "rgba(96,165,250,0.10)",
    glow: "rgba(37,99,235,0.18)",
  },
  cyan: {
    dot: "#22D3EE",
    border: "rgba(34,211,238,0.30)",
    soft: "rgba(34,211,238,0.10)",
    glow: "rgba(6,182,212,0.18)",
  },
  indigo: {
    dot: "#818CF8",
    border: "rgba(129,140,248,0.30)",
    soft: "rgba(129,140,248,0.10)",
    glow: "rgba(99,102,241,0.18)",
  },
};

export default function Expertise() {
  return (
    <section
      id="expertise"
      data-testid="expertise-section"
      className="relative py-24 md:py-32 overflow-hidden"
    >
      {/* Subtle cyber backdrop */}
      <div aria-hidden className="absolute inset-0 pointer-events-none">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(700px 380px at 10% 0%, rgba(37,99,235,0.10), transparent 60%), radial-gradient(700px 380px at 90% 100%, rgba(6,182,212,0.08), transparent 65%)",
          }}
        />
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage:
              "linear-gradient(rgba(96,165,250,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(96,165,250,0.6) 1px, transparent 1px)",
            backgroundSize: "72px 72px",
            maskImage:
              "radial-gradient(ellipse 80% 60% at 50% 40%, #000 50%, transparent 90%)",
            WebkitMaskImage:
              "radial-gradient(ellipse 80% 60% at 50% 40%, #000 50%, transparent 90%)",
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 md:px-10">
        {/* Section header */}
        <div className="flex items-center gap-3 mb-6">
          <span className="h-px w-8 bg-blue-500" />
          <span className="label-eyebrow">01 — Expertise</span>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-8 lg:gap-12 mb-14 md:mb-16 items-end">
          <h2 className="md:col-span-8 font-display font-bold text-3xl sm:text-4xl md:text-5xl lg:text-[3.25rem] tracking-tighter leading-[1.04] text-white">
            Six domains. <br />
            <span
              className="bg-clip-text text-transparent"
              style={{
                backgroundImage:
                  "linear-gradient(90deg,#67E8F9 0%,#60A5FA 50%,#A5B4FC 100%)",
              }}
            >
              One operational practice.
            </span>
          </h2>
          <p className="md:col-span-4 text-slate-400 leading-relaxed text-base md:text-lg">
            Curated capability stack — vendor-current, attacker-aware, and
            mapped to the controls your auditors actually screen for.
          </p>
        </div>

        {/* Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
          {CATEGORIES.map((c, i) => {
            const a = accentMap[c.accent];
            const Icon = c.icon;
            return (
              <motion.article
                key={c.code}
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, amount: 0.2 }}
                transition={{ duration: 0.55, delay: i * 0.06 }}
                data-testid={`expertise-card-${c.code}`}
                className="group relative rounded-2xl p-7 transition-all duration-300 hover:-translate-y-1 flex flex-col"
                style={{
                  background:
                    "linear-gradient(180deg,rgba(10,12,19,0.85) 0%,rgba(7,10,18,0.95) 100%)",
                  border: "1px solid rgba(255,255,255,0.08)",
                  boxShadow: "0 1px 0 rgba(255,255,255,0.03) inset",
                }}
              >
                {/* Hover glow */}
                <div
                  aria-hidden
                  className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-500 pointer-events-none"
                  style={{
                    background: `radial-gradient(360px 200px at 80% 0%, ${a.glow}, transparent 70%)`,
                  }}
                />
                {/* Top row */}
                <div className="relative flex items-start justify-between">
                  <div
                    className="h-11 w-11 rounded-lg grid place-items-center transition-transform group-hover:scale-105"
                    style={{
                      background: a.soft,
                      border: `1px solid ${a.border}`,
                      color: a.dot,
                    }}
                  >
                    <Icon size={20} />
                  </div>
                  <span
                    className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-600"
                    style={{ color: a.dot, opacity: 0.55 }}
                  >
                    {c.code}
                  </span>
                </div>

                {/* Title */}
                <h3 className="relative mt-6 font-display font-semibold text-xl md:text-[1.35rem] tracking-tight text-white leading-snug">
                  {c.title}
                </h3>

                {/* Skill chips */}
                <ul className="relative mt-5 flex flex-wrap gap-1.5 flex-1">
                  {c.skills.map((s) => (
                    <li
                      key={s}
                      className="text-[10px] font-mono uppercase tracking-wider px-2.5 py-1 rounded-full border text-slate-300 transition-colors group-hover:text-white"
                      style={{ borderColor: a.border, background: a.soft }}
                    >
                      {s}
                    </li>
                  ))}
                </ul>

                {/* Footer count */}
                <div
                  className="relative mt-6 pt-4 flex items-center justify-between font-mono text-[10px] uppercase tracking-[0.22em]"
                  style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}
                >
                  <span className="text-slate-500">
                    {String(c.skills.length).padStart(2, "0")} capabilities
                  </span>
                  <span
                    className="inline-flex items-center gap-1.5"
                    style={{ color: a.dot }}
                  >
                    <span className="h-1 w-1 rounded-full" style={{ background: a.dot }} />
                    operational
                  </span>
                </div>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
